import styled, { css } from 'styled-components';
import theme from 'src/theme';

// Enums for better type safety
export enum TextAlignment {
    LEFT = 'left',
    RIGHT = 'right',
}

// Enhanced props interface with better typing
interface InputElementWrapperProps {
    isValid: boolean;
    hasWarning?: boolean;
    labelMinWidth?: number | string;
    textAlign?: TextAlignment | string;
}

// Helper function to determine justification based on text alignment
const getJustification = (textAlign?: string): string => {
    if (!textAlign) return 'flex-end';
    return textAlign === TextAlignment.LEFT ? 'flex-start' : 'flex-end';
};

// Constants for reused values
const COLORS = {
    FOCUS_BORDER: '#478cdc',
    WARNING: theme.legacyColors.softErrorText,
    ERROR: theme.legacyColors.errorText,
    ERROR_BORDER: 'red',
};
// Validation style mixins for reuse
const validationStyles = {
    warning: css`
        color: ${COLORS.WARNING};
        input {
            border-color: ${COLORS.WARNING};
            color: ${COLORS.WARNING};
        }
        .ant-picker {
            border-color: ${COLORS.WARNING} !important;
        }
    `,
    error: css`
        color: ${COLORS.ERROR};
        input {
            border-color: ${COLORS.ERROR_BORDER};
            color: ${COLORS.ERROR_BORDER};
        }
        .ant-picker {
            border-color: ${COLORS.ERROR_BORDER} !important;
        }
    `,
};

// Text alignment mixin
const getTextAlignmentStyle = (textAlign?: TextAlignment | string) => css`
    justify-content: ${getJustification(textAlign)};
`;

// Label width mixin
const getLabelWidthStyle = (labelMinWidth?: number | string) => {
    if (!labelMinWidth) return '';
    return css`
        min-width: ${typeof labelMinWidth === 'number' ? `${labelMinWidth}px` : labelMinWidth};
    `;
};

// Main styled component that applies the validation and layout styles
export const InputElementWrapper = styled.div<InputElementWrapperProps>`
    position: relative;
    margin: 2px 0;

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    textarea {
        min-width: 100%;
        min-height: 52px;
        outline: none;
    }

    label {
        display: flex;
        flex: 1;
        justify-content: space-between;

        select {
            border: 1px solid #ccc;
            border-radius: 4px;
            padding: 2px 4px;
            outline: none;
        }

        input {
            padding: 0.1rem 0.35rem;
            outline: none;
        }

        input[type='number'],
        input.number {
            text-align: right;
            padding: 0 0.35rem;
            width: 60px;
        }

        input[type='checkbox'] {
            filter: grayscale(0.6);
        }

        input:hover {
            border-color: ${COLORS.FOCUS_BORDER};
        }

        input:focus,
        textarea:focus,
        select:focus {
            border-color: ${COLORS.FOCUS_BORDER};
            border-width: 1px;
        }

        .read-only-input {
            &.temperature-input {
                min-width: 30px;
            }
            &.textarea-input {
                width: 100%;
            }
            &.checkbox-input {
                min-width: 20px;
                width: 20px;
            }
            &.cargoTempLog-input {
                min-width: 45px;
                padding-right: 0.45rem;
            }
        }

        .input-value-description {
            display: inline-block;
            ${({ labelMinWidth }) => getLabelWidthStyle(labelMinWidth)}

            svg {
                margin-left: 1rem;
                cursor: help;
                color: coral;
            }
        }

        .input-value-wrapper {
            flex: 1 0 auto;
            display: flex;
            ${({ textAlign }) => getTextAlignmentStyle(textAlign)}
        }

        // .input-value {
        //     text-align: right;
        // }

        .input-unit {
            margin-left: 5px;
            width: 40px;
        }

        .input-cost {
            margin-right: 1rem;
            font-style: italic;
        }
    }

    ${({ hasWarning }) => hasWarning && validationStyles.warning}
    ${({ isValid }) => !isValid && validationStyles.error}
`;

export const ReadOnlyInput = styled.span`
    text-align: right;
`;

export const ReadOnlyFAWrapper = styled.div`
    display: flex;
`;
